import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Link2, Play, Loader2, CheckCircle, Globe, ArrowRight,
  BarChart3, RefreshCw, ExternalLink, ChevronDown, ChevronUp
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function InternalLinking() {
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyingSite, setApplyingSite] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [applyResults, setApplyResults] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/internal-links/stats`);
      setStats(res.data);
    } catch {}
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sites`);
      const active = (res.data || []).filter(s => s.status === "active");
      active.sort((a, b) => (b.posts_count || 0) - (a.posts_count || 0));
      setSites(active);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSites();
  }, [fetchStats, fetchSites]);

  const analyzeSite = async (siteId) => {
    setSelectedSite(siteId);
    setAnalyzing(true);
    setAnalysis(null);
    setApplyResults({});
    try {
      const res = await axios.get(`${API}/internal-links/analyze/${siteId}?limit=30`);
      setAnalysis(res.data);
    } catch {}
    setAnalyzing(false);
  };

  const applyToPost = async (postId) => {
    setApplying(postId);
    try {
      const res = await axios.post(`${API}/internal-links/apply-post/${postId}`);
      setApplyResults(prev => ({ ...prev, [postId]: res.data }));
      fetchStats();
    } catch {}
    setApplying(false);
  };

  const applyToSite = async (siteId) => {
    setApplyingSite(true);
    try {
      const res = await axios.post(`${API}/internal-links/apply-site/${siteId}?max_posts=30`);
      setAutoResult(res.data);
      fetchStats();
      if (selectedSite === siteId) analyzeSite(siteId);
    } catch {}
    setApplyingSite(false);
  };

  const autoRun = async () => {
    setAutoRunning(true);
    setAutoResult(null);
    try {
      const res = await axios.post(`${API}/internal-links/auto-run`);
      setAutoResult(res.data);
      fetchStats();
    } catch {}
    setAutoRunning(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="internal-linking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Link2 className="w-8 h-8 text-indigo-500" /> Linkowanie wewnetrzne
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Automatyczne wstawianie linkow miedzy artykulami na tej samej stronie (SEO)
          </p>
        </div>
      <ModuleDescription title="Jak dziala linkowanie wewnetrzne?">
        <p><strong>Linkowanie wewnetrzne</strong> automatycznie wstawia linki miedzy artykulami na tej samej stronie. To poprawia strukture SEO i pomaga Google lepiej indeksowac tresc.</p>
        <p><strong>Jak to dziala:</strong> Agent analizuje slowa kluczowe i tytuly artykulow, szuka naturalnych miejsc w tresci gdzie mozna wstawic link do powiazanego artykulu.</p>
        <p><strong>"Zastosuj"</strong> per post = dodaj linki do jednego artykulu | Przycisk Play przy stronie = linkuj cala strone | <strong>"Auto-linkuj wszystkie"</strong> = uruchom na wszystkich stronach.</p>
        <p>Agent SEO robi to automatycznie co 24h. Ten panel sluzy do recznego podgladu i kontroli.</p>
      </ModuleDescription>

        <Button
          onClick={autoRun}
          disabled={autoRunning}
          className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
          data-testid="auto-run-btn"
        >
          {autoRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {autoRunning ? "Linkuje..." : "Auto-linkuj wszystkie"}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{stats.total_posts_linked}</p>
              <p className="text-xs text-muted-foreground">Postow z linkami</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.total_links_added}</p>
              <p className="text-xs text-muted-foreground">Linkow dodanych</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.sites?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Stron z linkami</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {stats.total_posts_linked > 0 ? (stats.total_links_added / stats.total_posts_linked).toFixed(1) : "0"}
              </p>
              <p className="text-xs text-muted-foreground">Srednia linkow/post</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Auto-run result */}
      {autoResult && (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">
                Linkowanie zakonczone: {autoResult.total_links_added || 0} linkow w {autoResult.sites_processed || autoResult.processed || 0} stronach
                {autoResult.wp_updated > 0 && ` (${autoResult.wp_updated} WP zaktualizowanych)`}
              </span>
            </div>
            {autoResult.details && autoResult.details.length > 0 && (
              <div className="text-xs text-green-700 mt-1 space-y-0.5">
                {autoResult.details.slice(0, 5).map((d, i) => (
                  <div key={i}>{d.site_id?.substring(0, 8)}... : {d.links} linkow w {d.posts} postach</div>
                ))}
              </div>
            )}
            {autoResult.posts && autoResult.posts.length > 0 && (
              <div className="text-xs text-green-700 mt-1 space-y-0.5">
                {autoResult.posts.slice(0, 5).map((p, i) => (
                  <div key={i}>
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    {p.title?.substring(0, 50)}... : {p.links_added} linkow {p.wp_updated && <Badge className="ml-1 text-[7px] bg-green-200">WP OK</Badge>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sites list */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Globe className="w-4 h-4" /> Strony ({sites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {sites.filter(s => (s.posts_count || 0) >= 3).map(site => {
                const siteStats = stats?.sites?.find(ss => ss.site_id === site.id);
                return (
                  <div
                    key={site.id}
                    data-testid={`site-item-${site.id}`}
                    className={`flex items-center justify-between px-4 py-2.5 border-b cursor-pointer hover:bg-muted/30 transition-colors ${selectedSite === site.id ? "bg-indigo-50 border-l-2 border-l-indigo-500" : ""}`}
                    onClick={() => analyzeSite(site.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{site.name}</div>
                      <div className="text-[10px] text-muted-foreground">{site.posts_count || 0} postow</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {siteStats && (
                        <Badge className="text-[8px] bg-indigo-100 text-indigo-700">{siteStats.total_links} linkow</Badge>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); applyToSite(site.id); }}
                        disabled={applyingSite}
                        data-testid={`apply-site-${site.id}`}
                      >
                        <Play className="w-3.5 h-3.5 text-green-600" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Analysis */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" /> Analiza linkowania
              {analysis && !analyzing && (
                <Badge className="ml-2 text-xs bg-indigo-100 text-indigo-700">
                  {analysis.posts_with_opportunities} postow, {analysis.total_potential_links} linkow
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedSite && !analyzing && (
              <div className="text-center py-12 text-muted-foreground">
                <Link2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Wybierz strone z listy, zeby przeanalizowac mozliwosci linkowania</p>
              </div>
            )}

            {analyzing && (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-indigo-500" />
                <p className="text-sm text-muted-foreground">Analizuje posty...</p>
              </div>
            )}

            {analysis && !analyzing && (
              <ScrollArea className="h-[440px]">
                <div className="space-y-2">
                  {analysis.posts?.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">Brak mozliwosci linkowania w analizowanych postach</div>
                  )}
                  {analysis.posts?.map(post => {
                    const result = applyResults[post.post_id];
                    const isExpanded = expandedPost === post.post_id;
                    return (
                      <div key={post.post_id} className="border rounded-lg p-3" data-testid={`post-analysis-${post.post_id}`}>
                        <div className="flex items-center justify-between">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => setExpandedPost(isExpanded ? null : post.post_id)}
                          >
                            <div className="flex items-center gap-2">
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                              <span className="text-sm font-medium">{post.title?.substring(0, 70)}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground ml-5">
                              {post.existing_internal_links} istniejacych linkow | {post.potential_count} do dodania
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Badge className="text-xs bg-amber-100 text-amber-700">{post.potential_count}</Badge>
                            {result ? (
                              <Badge className="text-xs bg-green-100 text-green-700 gap-0.5">
                                <CheckCircle className="w-3 h-3" /> {result.links_added}
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs gap-1"
                                onClick={() => applyToPost(post.post_id)}
                                disabled={applying === post.post_id}
                                data-testid={`apply-post-${post.post_id}`}
                              >
                                {applying === post.post_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                                Zastosuj
                              </Button>
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-2 ml-5 space-y-1.5">
                            {(result?.links || post.potential_links || []).map((link, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs bg-muted/30 rounded px-2.5 py-1.5">
                                <Link2 className="w-3 h-3 text-indigo-500 shrink-0" />
                                <span className="font-medium text-indigo-700">"{link.anchor}"</span>
                                <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline truncate flex items-center gap-0.5"
                                >
                                  {link.target_title?.substring(0, 50) || link.url?.substring(0, 50)}
                                  <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                                </a>
                              </div>
                            ))}
                            {result?.wp_update && (
                              <div className="text-[10px] text-muted-foreground">
                                WordPress: {result.wp_update.ok ? <span className="text-green-600">Zaktualizowany</span> : <span className="text-red-500">Blad ({result.wp_update.status})</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-site stats */}
      {stats?.sites?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Historia linkowania per strona</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="bg-muted/30">
                <tr className="border-b">
                  <th className="py-2 px-4 text-left font-medium">Strona</th>
                  <th className="py-2 px-4 text-right font-medium">Posty z linkami</th>
                  <th className="py-2 px-4 text-right font-medium">Linkow dodanych</th>
                  <th className="py-2 px-4 text-right font-medium">Ostatnio</th>
                </tr>
              </thead>
              <tbody>
                {stats.sites.map(s => (
                  <tr key={s.site_id} className="border-b hover:bg-muted/20">
                    <td className="py-2 px-4 font-medium">{s.site_name}</td>
                    <td className="py-2 px-4 text-right">{s.posts_linked}</td>
                    <td className="py-2 px-4 text-right font-bold text-indigo-600">{s.total_links}</td>
                    <td className="py-2 px-4 text-right text-muted-foreground">{s.last_linked?.substring(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
